<template>
  <etapa-formulario>
    <secao-formulario rotulo="Houve lesão" :icone="false" campos-obrigatorios />

    <div
      class="etapa-formulario__campo etapa-formulario__campo--md-6 etapa-formulario__campo--xl-4"
    >
      <campo-selecao
        obrigatorio
        :carregando="carregandoOpcoes"
        :desabilitado="carregandoOpcoes || !campoHabilitado('houveLesao')"
        :opcoes="opcoesEstadoSaude.houveLesao"
        :value="houveLesaoSelecionada"
        :erro="validacao.houveLesao"
        :placeholder="'Selecione se houve lesão ou não'"
        rotulo="Houve lesão do(a) Cooperado(a)?"
        @input="atualizarCampo('houveLesao', $event)"
      />
    </div>

    <transition name="fade">
      <div
        v-if="ativarCamposEstadoSaude"
        class="etapa-formulario__campo etapa-formulario__campo--md-6 etapa-formulario__campo--xl-4"
      >
        <campo-selecao
          obrigatorio
          :carregando="carregandoOpcoes"
          :desabilitado="carregandoOpcoes || !campoHabilitado('tipoLesao')"
          rotulo="Tipo da Lesão"
          :value="tipoLesaoSelecionada"
          :erro="validacao.tipoLesao"
          placeholder="Selecione o tipo da lesão"
          :opcoes="opcoesTipoLesao"
          @input="atualizarCampo('tipoLesao', $event)"
        />
      </div>
    </transition>

    <transition name="fade">
      <div
        v-if="ativarCamposComplementaresEstadoSaude"
        class="etapa-formulario__campo etapa-formulario__campo--md-6 etapa-formulario__campo--xl-4"
      >
        <campo-selecao
          obrigatorio
          :carregando="carregandoOpcoes"
          :desabilitado="
            carregandoOpcoes || !campoHabilitado('necessitaAfastamento')
          "
          rotulo="Necessita de afastamento?"
          :value="necessitaAfastamentoSelecionado"
          :erro="validacao.necessitaAfastamento"
          :placeholder="'Selecione um afastamento'"
          :opcoes="opcoesEstadoSaude.necessitaAfastamento"
          @input="atualizarCampo('necessitaAfastamento', $event)"
        />
      </div>
    </transition>

    <transition name="fade">
      <div
        v-if="ativarCamposComplementaresEstadoSaude"
        class="etapa-formulario__campo etapa-formulario__campo--md-6 etapa-formulario__campo--xl-12"
      >
        <campo-textarea
          obrigatorio
          :value="value.descricaoLesao"
          :erro="validacao.descricaoLesao"
          :desabilitado="!campoHabilitado('descricaoLesao')"
          :maxlength="4000"
          :minimo="20"
          exibir-contador
          :exibir-aviso-limite="false"
          rotulo="Informe a lesão e a gravidade"
          :placeholder="'Informe a lesão e a gravidade'"
          @input="atualizarCampo('descricaoLesao', $event)"
        />
      </div>
    </transition>
  </etapa-formulario>
</template>

<script lang="js">
import EtapaFormulario from "../EtapaFormulario.vue";
import SecaoFormulario from "../SecaoFormulario.vue";
import CampoSelecao from "../campos/CampoSelecao.vue";
import CampoTextarea from "../campos/CampoTextarea.vue";
import { validarEstadoSaude, validarCampoEstadoSaude } from "../../../domain/validators/estadoSaude.validator.js";
import { tipoLesaoEhNao, tipoLesaoEhObito } from "../../../domain/rules/estadoSaude.js";

/**
 * Etapa "Estado de Saúde (Lesão)" do cadastro de novo relato.
 *
 * Pergunta sobre lesão do(a) cooperado(a), tipo de lesão, necessidade de
 * afastamento e descrição da lesão/gravidade, validando os campos obrigatórios antes
 * de emitir `salvar`/`proxima`.
 *
 * Os campos de seleção usam `campo-selecao` (padrão do módulo). As opções
 * selecionadas são resolvidas pelos valores retornados pelos endpoints.
 *
 * @prop {Object} value - dados da seção (ocorrencia.saude no container)
 * @prop {Object} opcoesEstadoSaude - opções dos selects (houveLesao, tipoLesao, necessitaAfastamento)
 *
 * @emits input {Object} seção atualizada a cada campo alterado
 * @emits salvar {Object} quando salvo com obrigatórios preenchidos
 * @emits proxima  quando avançado com obrigatórios preenchidos
 */
export default {
  name: "EstadoSaude",

  inject: {
    modoVisualizacao: {
      type: Boolean,
      default: false
    }
  },

  components: {
    CampoSelecao,
    CampoTextarea,
    EtapaFormulario,
    SecaoFormulario
  },

  props: {
    value: {
      type: Object,
      required: true
    },

    opcoesEstadoSaude: {
      type: Object,
      default: () => ({
        houveLesao: [],
        necessitaAfastamento: [],
        tipoLesao: []
      })
    },

    carregandoOpcoes: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      /** @type {Object<string, string>} mensagens de erro por campo. */
      validacao: {}
    };
  },

  computed: {
    houveLesaoSelecionada() {
      return this.opcaoSelecionada(
        this.value.houveLesao,
        this.opcoesEstadoSaude.houveLesao
      );
    },
    necessitaAfastamentoSelecionado() {
      return this.opcaoSelecionada(
        this.value.necessitaAfastamento,
        this.opcoesEstadoSaude.necessitaAfastamento
      );
    },
    tipoLesaoSelecionada() {
      return this.opcaoSelecionada(
        this.value.tipoLesao,
        this.opcoesEstadoSaude.tipoLesao
      );
    },
    opcoesTipoLesao() {
      return (this.opcoesEstadoSaude.tipoLesao || []).filter(
        opcao => !tipoLesaoEhNao(opcao)
      );
    },
    ativarCamposEstadoSaude() {
      return this.extrairValor(this.value.houveLesao) === "S";
    },
    ativarCamposComplementaresEstadoSaude() {
      return (
        this.ativarCamposEstadoSaude &&
        this.temValorPreenchido(this.value.tipoLesao) &&
        !tipoLesaoEhNao(this.value.tipoLesao) &&
        !tipoLesaoEhObito(this.value.tipoLesao)
      );
    },
    sequenciaSaude() {
      const campos = ["houveLesao", "tipoLesao"];
      if (this.ativarCamposComplementaresEstadoSaude) {
        campos.push("necessitaAfastamento", "descricaoLesao");
      }
      return campos;
    }
  },

  methods: {
    extrairValor(valor) {
      if (valor == null) return valor;
      if (typeof valor === "object") return valor.value ?? null;
      return valor;
    },
    opcaoSelecionada(valor, opcoes = []) {
      const atual = this.extrairValor(valor);
      if (atual === null || atual === undefined || atual === "") return null;
      return (
        (opcoes || []).find(
          opcao => String(opcao.value) === String(atual)
        ) || { value: atual, label: String(atual) }
      );
    },
    temValorPreenchido(val) {
      if (val === null || val === undefined || val === "") return false;
      if (typeof val === "object") {
        const inner = val.value !== undefined ? val.value : val.label;
        return inner !== null && inner !== undefined && inner !== "";
      }
      return String(val).trim() !== "";
    },
    campoHabilitado(campo) {
      if (this.modoVisualizacao) return false;
      const ordem = this.sequenciaSaude;
      const indice = ordem.indexOf(campo);
      if (indice <= 0) return true;
      for (let i = 0; i < indice; i += 1) {
        if (!this.temValorPreenchido(this.value[ordem[i]])) return false;
      }
      return true;
    },
    atualizarCampo(campo, valor) {
      const valorNormalizado = this.extrairValor(valor);
      const atualizado = { ...this.value, [campo]: valorNormalizado };

      if (campo === "houveLesao") {
        atualizado.tipoLesao = valorNormalizado === "N" ? 1 : null;
        atualizado.necessitaAfastamento = null;
        atualizado.descricaoLesao = "";
        this.$delete(this.validacao, "tipoLesao");
        this.$delete(this.validacao, "necessitaAfastamento");
        this.$delete(this.validacao, "descricaoLesao");
      }

      if (
        campo === "tipoLesao" &&
        (tipoLesaoEhObito(valorNormalizado) ||
          tipoLesaoEhObito(this.value.tipoLesao))
      ) {
        atualizado.necessitaAfastamento = null;
        atualizado.descricaoLesao = "";
        this.$delete(this.validacao, "necessitaAfastamento");
        this.$delete(this.validacao, "descricaoLesao");
      }

      this.$emit("input", atualizado);

      const erro = validarCampoEstadoSaude(campo, valorNormalizado, atualizado);
      if (erro) {
        if (this.validacao[campo] || String(valorNormalizado ?? "").trim().length > 0) {
          this.$set(this.validacao, campo, erro);
        }
      } else {
        this.$delete(this.validacao, campo);
      }
      if (this.extrairValor(atualizado.houveLesao) !== "S") {
        this.$delete(this.validacao, "tipoLesao");
        this.$delete(this.validacao, "necessitaAfastamento");
        this.$delete(this.validacao, "descricaoLesao");
      }
    },

    salvar() {
      if (this.validar()) {
        this.$emit("salvar", this.value);
      } else {
        this.$tools?.alerta?.("Preencha corretamente todos os campos.", "warning");
      }
    },

    proximaAba() {
      if (this.validar()) {
        this.$emit("proxima");
      } else {
        this.$tools?.alerta?.("Preencha corretamente todos os campos.", "warning");
      }
    },

    validar() {
      if (this.modoVisualizacao) return true;
      const { isValid, erros } = validarEstadoSaude(this.value);
      this.validacao = erros;
      return isValid;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../styles/_main";
@import "../../../styles/layout/_transitions";
</style>
